/**
 * REST tag client for the proxy's canonical tag registry surface
 * Replaces the former ConnectRPC `TagService` client
 * (`tag-connect.ts`): registry list, batch registration, per-tag reads/writes,
 * and history lifecycle controls all live under `/api/v1/ontology/tags*`.
 * They run behind the proxy's normal Chi auth plus Branch/Site middleware
 * (the ConnectRPC dispatch used to bypass those).
 *
 * Live exported hooks keep their ConnectRPC-era names/signatures
 * (`useListTagsQuery`, `useGetTagSeriesQuery`, …). Reading summaries and
 * inventory health were always REST and live here alongside the registry
 * client.
 *
 * Identity note: the *registry* served by these endpoints is the
 * acquisition/readings/health backend keyed on (source_url, tag_path).
 * Canonical tag *identity* (enumeration, pickers, validation) is the graph
 * `:Tag` node — see `graph-tags.ts`.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { DbSyncMode, DbTransport } from '@atbash-labs/ontology-db'
import {
  tagScrapeModeFromWire,
  tagSourceTypeFromWire,
  type TagScrapeMode,
  type TagSourceTypeName
} from '@atbash-labs/ontology-tag-registration'
import { PROXY_API_V1_URL, createOntologyHeaders } from '../core'
import { invalidateTagInventoryQueryCache } from './tag-inventory'
import { getOntologyServiceBaseUrl } from '../../config'
import { trackDataFlight } from '../../telemetry/navigation-timing'

export type { TagSourceTypeName }
export type TagScrapeModeName = TagScrapeMode

// ---------------------------------------------------------------------------
// Base URL + headers
// ---------------------------------------------------------------------------

function tagsApiUrl(path: string): string {
  return `${PROXY_API_V1_URL}${path}`
}

/**
 * Full ontology header set (X-API-Key, Authorization bearer, branch/site,
 * traceparent) for the tag REST endpoints. The registry itself is not
 * branch-scoped, but sending the scoping headers keeps every tag request
 * uniform with the rest of the /api/v1 surface.
 */
function tagsApiHeaders(): Headers {
  return createOntologyHeaders()
}

/** Parses a JSON body, treating an unreadable one as empty. */
async function readJsonObject(resp: Response): Promise<Record<string, unknown>> {
  try {
    const body = (await resp.json()) as unknown
    if (body && typeof body === 'object') return body as Record<string, unknown>
  } catch {
    // Non-JSON body — callers fall back to the status line.
  }
  return {}
}

/** The server's own error message when it sent one, else the status line. */
function responseErrorDetail(resp: Response, body: Record<string, unknown>): string {
  const message = body.message
  if (typeof message === 'string' && message) return message
  return `${resp.status} ${resp.statusText}`
}

async function requestJson<T>(
  path: string,
  init: RequestInit & { signal?: AbortSignal } = {}
): Promise<T> {
  const resp = await fetch(tagsApiUrl(path), {
    ...init,
    headers: tagsApiHeaders()
  })
  if (!resp.ok) {
    throw new Error(responseErrorDetail(resp, await readJsonObject(resp)))
  }
  if (resp.status === 204) return undefined as T
  return (await resp.json()) as T
}

// ---------------------------------------------------------------------------
// TS-friendly view types (stable across the ConnectRPC → REST migration)
// ---------------------------------------------------------------------------

export type TagStateName = 'enabled' | 'disabled' | 'unspecified'
export type TagScrapeStateName = 'idle' | 'running' | 'error' | 'unspecified'

export interface TagRecord {
  name: string
  id: string
  /** Graph `:Tag.id` used to join registry rows to ontology tags. */
  nodeId: string | null
  tagPath: string
  // Provider portion of tagPath extracted by the proxy. "default"
  // for "[default]a/b/c"; empty when the path carries no provider
  // prefix. Server-derived — consume rather than re-parse.
  provider: string
  // Ordered segments after the provider. ["a","b","c"] for
  // "[default]a/b/c"; the last element is the leaf, everything
  // before is the folder. Server-derived.
  pathSegments: string[]
  sourceUrl: string
  sourceType: TagSourceTypeName
  // Acquisition mechanism: poll (unified poller), subscribe (OPC UA
  // subscription manager), or stream (MTConnect stream manager).
  scrapeMode: TagScrapeModeName
  // Registered scrape cadence in milliseconds (0 when the registry row
  // carries no cadence). Used to derive per-source staleness floors and
  // connectivity — a 15-min DB sample isn't "stale" at the 120s tag floor.
  frequencyMs: number
  // Physical OPC NodeId for subscribe-mode rows; null when poll-only or absent.
  opcNodeId: string | null
  displayLabel: string
  state: TagStateName
  createdAt: string
  updatedAt: string
}

export interface TagScrapeStatusRecord {
  tagName: string
  tagId: string
  state: TagScrapeStateName
  lastScrapeTime: string | null
  lastError: string | null
  scrapeCount: number
  updatedAt: string
}

export type TagCredentialProvider = 'webdev' | 'opc' | 'mtconnect' | 'database' | 'pi' | 'canary'

export interface TagCredentialPosture {
  provider: TagCredentialProvider
  hasPassword: boolean
  anonymousAccess: boolean | null
}

export interface TagScrapeConfigRecord {
  tagId: string
  frequencyMs: number
  relayFallbackEnabled: boolean
  enabled: boolean
  updatedAt: string
  vendor: string
  protocol: string
  acquisitionQuarantined: boolean
  quarantineRule: string | null
  quarantineNote: string | null
  relayGatewayUrl: string | null
  relayWebDevProject: string | null
  relayOpcServer: string | null
  relayNodeId: string | null
  credential: TagCredentialPosture | null
}

export interface TagSeriesPointRecord {
  // ISO-8601 UTC timestamp of the bucket start.
  time: string
  // Number of readings in the bucket.
  count: number
  // Aggregates across the bucket. Always populated when count > 0.
  avg?: number
  min?: number
  max?: number
}

export interface TagReadingRecord {
  tagName: string
  time: string
  tagType: string
  quality: string
  isGood: boolean
  valueDouble: number | null
  valueInt: number | null
  valueBool: boolean | null
  valueString: string | null
  valueTimestamp: string | null
  valueJson: unknown
}

// High-level category the proxy's statistics package infers from a tag's
// historical readings (see internal/ontology/tag/stats in proxy-server).
//   - numeric:      continuous/discrete numeric measurement
//   - boolean_enum: boolean or low-cardinality state/mode code
//   - other:        free-form string, timestamp, JSON, or too few readings
export type TagClassName = 'numeric' | 'boolean_enum' | 'other'

// Numeric branch of the statistics summary. Nullable aggregates are `null`
// (not 0) when the window has no good numeric readings.
export interface TagNumericSummary {
  windowSeconds: number
  count: number
  min: number | null
  max: number | null
  range: number | null
  mean: number | null
  stddev: number | null
  coefficientOfVariation: number | null
  median: number | null
  p5: number | null
  p25: number | null
  p75: number | null
  p95: number | null
  p99: number | null
  iqr: number | null
  timeWeightedAverage: number | null
  lastValue: number | null
  lastTime: string | null
  lastZScore: number | null
  lastWithin1Sigma: boolean
  lastWithin2Sigma: boolean
  lastWithin3Sigma: boolean
}

// One discrete state's slice of the window in the enum/boolean branch.
export interface TagStateDistribution {
  state: string
  sampleCount: number
  samplePercent: number
  secondsInState: number
  timeWeightedPercent: number
}

// Enum/boolean branch of the statistics summary.
export interface TagStateSummary {
  windowSeconds: number
  count: number
  badQualitySamples: number
  states: TagStateDistribution[]
  currentState: string | null
  currentStateSince: string | null
  currentDurationSeconds: number | null
  transitionCount: number
}

// Freshness / density / quality block, present on every summary response.
export interface TagDataQuality {
  windowSeconds: number
  sampleCount: number
  goodCount: number
  badCount: number
  goodPercent: number
  samplesPerMinute: number
  firstReadingAt: string | null
  lastReadingAt: string | null
  stalenessSeconds: number | null
  longestGapSeconds: number | null
  meanGapSeconds: number | null
  /**
   * Quality of the newest reading the server could see, or null when it knows
   * of none. Not derivable from the counts above: a tag that faults part-way
   * through the window still reports mostly good samples while being broken
   * right now.
   */
  lastReadingIsGood: boolean | null
}

// Response of GET /api/v1/ontology/tags/stats/summary. `numeric` and `state` are
// mutually exclusive (decided by `class`); `dataQuality` is always set.
export interface TagSummaryRecord {
  class: TagClassName
  classificationReason: string
  windowSeconds: number
  numeric: TagNumericSummary | null
  state: TagStateSummary | null
  dataQuality: TagDataQuality
}

// ---------------------------------------------------------------------------
// Wire → record mappers (snake_case REST JSON → camelCase records)
// ---------------------------------------------------------------------------

const TAG_NAME_PREFIX = 'tags/'

function tagIdFromName(name: string): string {
  return name.startsWith(TAG_NAME_PREFIX) ? name.slice(TAG_NAME_PREFIX.length) : name
}

function tagNameFromId(id: string): string {
  return id.startsWith(TAG_NAME_PREFIX) ? id : `${TAG_NAME_PREFIX}${id}`
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

function nullableNum(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

function nullableStr(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null
}

function nullableBool(v: unknown): boolean | null {
  return typeof v === 'boolean' ? v : null
}

function toPollSettings(j: Record<string, unknown>): TagPollSettings {
  return { pollFrequencyMs: num(j.poll_frequency_ms), updatedAt: nullableStr(j.updated_at) }
}

function scrapeStateFromWire(v: unknown): TagScrapeStateName {
  return v === 'idle' || v === 'running' || v === 'error' ? v : 'unspecified'
}

export function tagRecordFromJson(raw: unknown): TagRecord {
  const j = (raw ?? {}) as Record<string, unknown>
  const id = str(j.id)
  return {
    name: tagNameFromId(id),
    id,
    nodeId: nullableStr(j.node_id),
    tagPath: str(j.tag_path),
    provider: str(j.provider),
    pathSegments: Array.isArray(j.path_segments)
      ? (j.path_segments as unknown[]).filter((s): s is string => typeof s === 'string')
      : [],
    sourceUrl: str(j.source_url),
    sourceType: tagSourceTypeFromWire(j.source_type),
    scrapeMode: tagScrapeModeFromWire(j.scrape_mode),
    frequencyMs: num(j.frequency_ms),
    opcNodeId: nullableStr(j.opc_node_id),
    displayLabel: str(j.display_label),
    state: j.enabled === false ? 'disabled' : 'enabled',
    createdAt: str(j.created_at),
    updatedAt: str(j.updated_at)
  }
}

function scrapeStatusFromJson(raw: unknown): TagScrapeStatusRecord {
  const j = (raw ?? {}) as Record<string, unknown>
  const tagId = str(j.tag_id)
  return {
    tagName: tagNameFromId(tagId),
    tagId,
    state: scrapeStateFromWire(j.state),
    lastScrapeTime: nullableStr(j.last_scrape_at),
    lastError: nullableStr(j.last_error),
    scrapeCount: num(j.scrape_count),
    updatedAt: str(j.updated_at)
  }
}

function credentialProviderFromWire(value: unknown): TagCredentialProvider | null {
  return value === 'webdev' ||
    value === 'opc' ||
    value === 'mtconnect' ||
    value === 'database' ||
    value === 'pi' ||
    value === 'canary'
    ? value
    : null
}

export function scrapeConfigFromJson(raw: unknown): TagScrapeConfigRecord {
  const j = (raw ?? {}) as Record<string, unknown>
  const credentialJson =
    j.credential && typeof j.credential === 'object'
      ? (j.credential as Record<string, unknown>)
      : null
  const credentialProvider = credentialProviderFromWire(credentialJson?.provider)
  return {
    tagId: str(j.tag_id),
    frequencyMs: num(j.frequency_ms),
    relayFallbackEnabled: j.relay_fallback_enabled === true,
    enabled: j.enabled === true,
    updatedAt: str(j.updated_at),
    vendor: str(j.vendor),
    protocol: str(j.protocol),
    acquisitionQuarantined: j.acquisition_quarantined === true,
    quarantineRule: nullableStr(j.quarantine_rule),
    quarantineNote: nullableStr(j.quarantine_note),
    relayGatewayUrl: nullableStr(j.relay_gateway_url),
    relayWebDevProject: nullableStr(j.relay_webdev_project),
    relayOpcServer: nullableStr(j.relay_opc_server),
    relayNodeId: nullableStr(j.relay_node_id),
    credential:
      credentialJson && credentialProvider
        ? {
            provider: credentialProvider,
            hasPassword: credentialJson.has_password === true,
            anonymousAccess: nullableBool(credentialJson.anonymous_access)
          }
        : null
  }
}

function seriesPointFromJson(raw: unknown): TagSeriesPointRecord {
  const j = (raw ?? {}) as Record<string, unknown>
  const avg = nullableNum(j.avg)
  const min = nullableNum(j.min)
  const max = nullableNum(j.max)
  return {
    time: str(j.time),
    count: num(j.count),
    ...(avg === null ? {} : { avg }),
    ...(min === null ? {} : { min }),
    ...(max === null ? {} : { max })
  }
}

function readingFromJson(tagName: string, raw: unknown): TagReadingRecord {
  const j = (raw ?? {}) as Record<string, unknown>
  return {
    tagName,
    time: str(j.time),
    tagType: str(j.tag_type),
    quality: str(j.quality),
    isGood: j.is_good === true,
    valueDouble: nullableNum(j.value_double),
    valueInt: nullableNum(j.value_int),
    valueBool: typeof j.value_bool === 'boolean' ? j.value_bool : null,
    valueString: nullableStr(j.value_string),
    valueTimestamp: nullableStr(j.value_timestamp),
    valueJson: j.value_json ?? null
  }
}

// ---------------------------------------------------------------------------
// Client (typed hand-written surface)
// ---------------------------------------------------------------------------

export interface ListTagsArgs {
  pageSize?: number
  pageToken?: string
  filter?: string
}

export interface ListTagsResult {
  tags: TagRecord[]
  nextPageToken: string
  totalSize: number
}

export interface CreateTagArgs {
  tag: {
    tagPath: string
    sourceUrl: string
    sourceType: TagSourceTypeName
    displayLabel?: string
    state?: TagStateName
    scrapeMode?: TagScrapeModeName
  }
  initialScrapeConfig?: {
    frequencyMs: number
  }
}

export interface UpdateTagScrapeConfigArgs {
  name: string
  frequencyMs?: number
  relayFallbackEnabled?: boolean
  enabled?: boolean
}

export interface TagContainerScope {
  id: string
  kind: 'provider' | 'folder' | 'udt'
}

export interface SetTagsEnabledArgs {
  tagIds?: string[]
  nodeIds?: string[]
  containerScope?: TagContainerScope
  searchQuery?: string
  enabled: boolean
  preview: boolean
}

export interface SetTagsEnabledResult {
  preview: boolean
  enabled: boolean
  selectedCount: number
  registeredCount: number
  graphOnlyCount: number
  updatedCount: number
  resolvedNodeIds: string[]
}

export interface ListTagReadingsArgs {
  parent: string
  pageSize?: number
  pageToken?: string
  windowSecs?: number
}

export interface ListTagReadingsResult {
  readings: TagReadingRecord[]
  nextPageToken: string
}

export interface GetTagSeriesArgs {
  parent: string
  // Lookback window in seconds. Server clamps to ≤ 7 days.
  windowSecs: number
  // Bucket width in seconds. Server raises this if window/bucket would
  // exceed the response cap.
  bucketSecs: number
}

export interface GetTagSeriesResult {
  tag: string
  points: TagSeriesPointRecord[]
  // Echoed clamped values — may differ from what the caller requested.
  windowSecs: number
  bucketSecs: number
}

export interface TagSummaryArgs {
  // The tag's current source URL. Kept for raw-reading compatibility.
  sourceUrl: string
  // Retained for callers (and parity with the registry); not part of the
  // readings identity, so it is not sent on the statistics request.
  sourceType: TagSourceTypeName
  tagPath: string
  // Stable registry identity used by the Timescale historian when present.
  tagId?: string
  // Optional lookback window in seconds (default 1h server-side, max
  // 30d). Omit to use the server default.
  windowSecs?: number
}

// Selector for the batch statistics summary endpoint. Registered tags carry
// tagId; source/path remain the fallback for raw readings.
export interface TagSummaryBatchItem {
  sourceUrl: string
  sourceType: TagSourceTypeName
  tagPath: string
  tagId?: string
}

export interface BatchGetTagSummariesArgs {
  items: TagSummaryBatchItem[]
  // Optional lookback window in seconds; defaults to the server default
  // (1h) when omitted.
  windowSecs?: number
}

// One entry in the batch response — either summary populated (success) or
// error populated (per-item failure). The selector is echoed back so
// callers can re-key by tagPath without depending on slice order.
export interface BatchTagSummaryEntry {
  sourceUrl: string
  tagPath: string
  summary: TagSummaryRecord | null
  error: string | null
}

export interface BatchGetTagSummariesResult {
  windowSeconds: number
  items: BatchTagSummaryEntry[]
}

export interface DeregisterMtcLiveReadingsArgs {
  agentUrl: string
}

export interface DeregisterMtcLiveReadingsResult {
  tagsDeregistered: number
  endpointDeleted: boolean
}

export interface OrphanCleanupPreviewArgs {
  /** Case-insensitive substring match on tag path or source URL. */
  search?: string
  page?: number
  pageSize?: number
  /**
   * Force a fresh classification instead of the server's short-lived memo.
   * Set when opening a preview; leave off while paging, so the list can't
   * shift under the reader.
   */
  refresh?: boolean
}

export interface OrphanTagRow {
  tagPath: string
  sourceUrl: string
}

export interface OrphanGatewayGroup {
  sourceUrl: string
  tags: number
  endpointRemoved: boolean
}

export interface OrphanCleanupPreview {
  /** Identifies this exact set; the delete must quote it back. */
  fingerprint: string
  totalTags: number
  totalEndpoints: number
  totalReadings: number
  /**
   * `totalReadings` is a floor rather than the exact count: counting stopped
   * once the history was clearly in the millions. Render it as "N+".
   */
  totalReadingsCapped: boolean
  gateways: OrphanGatewayGroup[]
  /** One page of the deletion list, after `search` is applied. */
  rows: OrphanTagRow[]
  matchedTags: number
  page: number
  pageSize: number
}

export interface CleanupOrphanTagsArgs {
  /** From the preview that was reviewed. A stale value fails the run. */
  fingerprint: string
}

export interface StartOrphanCleanupResult {
  /** The background run doing the delete. Poll it for progress. */
  jobId: string
  /**
   * A run was already under way, so this call attached to that one instead of
   * starting a second fleet-wide delete.
   */
  alreadyRunning: boolean
}

/** Where a cleanup run has got to. `''` before the run reports anything. */
export type OrphanCleanupPhase = '' | 'classifying' | 'readings' | 'tags' | 'endpoints' | 'done'

export interface OrphanCleanupProgress {
  phase: OrphanCleanupPhase
  /** Size of the reviewed set, and how much of it has been cleared. */
  totalTags: number
  pairsDone: number
  readingsDeleted: number
  tagsDeleted: number
  endpointsDeleted: number
}

export interface OrphanCleanupRun {
  jobId: string
  /** `processing` while working, then `done` or `error`. */
  state: string
  error: string
  progress: OrphanCleanupProgress
}

export interface OrphanCleanupStatus {
  running: boolean
  jobId: string
}

export type AcquisitionTargetMode = 'subscribe' | 'poll'

export interface AcquisitionModeCount {
  sourceUrl: string
  sourceType: string
  scrapeMode: string
  count: number
}

export interface AcquisitionOPCEndpoint {
  endpointUrl: string
  hasSavedCredentials: boolean
  anonymousAccess: boolean
}

export interface AcquisitionSummary {
  pollCount: number
  subscribeCount: number
  sourceUrls: AcquisitionModeCount[]
  opcEndpoints: AcquisitionOPCEndpoint[]
}

/** Org-wide unified tag poller cadence (`GET/PUT /ontology/tags:pollSettings`). */
export interface TagPollSettings {
  pollFrequencyMs: number
  /** Null until the org first saves a cadence (the proxy default applies). */
  updatedAt: string | null
}

export interface ConvertAcquisitionConnection {
  endpointUrl?: string
  anonymousAccess?: boolean
  securityPolicies?: string
  usernameSource?: string
  username?: string
  password?: string
}

export interface ConvertAcquisitionModeArgs {
  targetMode: AcquisitionTargetMode
  httpGatewayUrl: string
  opcEndpointUrl: string
  connection?: ConvertAcquisitionConnection
  tagIds?: string[]
  nodeIds?: string[]
  containerScope?: TagContainerScope
  searchQuery?: string
  preview?: boolean
}

export interface ConvertAcquisitionFailure {
  tagId: string
  tagPath: string
  reason: string
}

export interface ConvertAcquisitionSkip {
  tagId: string
  tagPath: string
  reason: string
  /** Present when a graph selection has no corresponding registry row. */
  nodeId?: string
}

export interface ConvertAcquisitionModeResult {
  converted: number
  skipped: number
  skippedDetails: ConvertAcquisitionSkip[]
  failed: ConvertAcquisitionFailure[]
}

export interface ITagClient {
  listTags(req: ListTagsArgs): Promise<ListTagsResult>
  createTag(req: CreateTagArgs): Promise<TagRecord>
  deleteTag(req: { name: string }): Promise<void>
  getTagScrapeConfig(req: { name: string }): Promise<TagScrapeConfigRecord>
  updateTagScrapeConfig(req: UpdateTagScrapeConfigArgs): Promise<TagScrapeConfigRecord>
  setTagsEnabled(req: SetTagsEnabledArgs): Promise<SetTagsEnabledResult>
  getTagScrapeStatus(req: { name: string }): Promise<TagScrapeStatusRecord>
  listTagReadings(req: ListTagReadingsArgs): Promise<ListTagReadingsResult>
  getTagSeries(req: GetTagSeriesArgs): Promise<GetTagSeriesResult>
  getTagSummary(req: TagSummaryArgs): Promise<TagSummaryRecord>
  batchGetTagSummaries(req: BatchGetTagSummariesArgs): Promise<BatchGetTagSummariesResult>
  deregisterMtcLiveReadings(
    req: DeregisterMtcLiveReadingsArgs
  ): Promise<DeregisterMtcLiveReadingsResult>
  previewOrphanCleanup(req: OrphanCleanupPreviewArgs): Promise<OrphanCleanupPreview>
  startOrphanCleanup(req: CleanupOrphanTagsArgs): Promise<StartOrphanCleanupResult>
  getOrphanCleanupRun(req: { jobId: string }): Promise<OrphanCleanupRun>
  getOrphanCleanupStatus(): Promise<OrphanCleanupStatus>
  getAcquisitionSummary(): Promise<AcquisitionSummary>
  convertAcquisitionMode(req: ConvertAcquisitionModeArgs): Promise<ConvertAcquisitionModeResult>
  getPollSettings(): Promise<TagPollSettings>
  updatePollSettings(req: { pollFrequencyMs: number }): Promise<TagPollSettings>
}

// ---------------------------------------------------------------------------
// Client override (for tests)
// ---------------------------------------------------------------------------
//
// Tests can inject a fully-typed in-memory ITagClient so specs exercise the
// page UI plus the hook surface without a network. Production code never
// sees this branch.

let clientOverride: ITagClient | null = null

export function setTagClient(client: ITagClient | null): void {
  clientOverride = client
}

export function resetTagClient(): void {
  clientOverride = null
}

/**
 * Wrap every tag-client method so its in-flight read feeds per-screen TTFD.
 * The Tags page loads through this client — the REST registry
 * reads and the plain-fetch statistics methods alike — not RTK Query, so the
 * in-flight middleware never sees it; tracking at the one client chokepoint
 * covers every method uniformly. `trackDataFlight` is a no-op outside an
 * active navigation window, so post-load refetches and mutations are
 * unaffected.
 */
function withDataFlightTracking(client: ITagClient): ITagClient {
  const wrapped: Record<string, unknown> = {}
  for (const [name, value] of Object.entries(client)) {
    const fn = value as (...args: unknown[]) => unknown
    wrapped[name] = (...args: unknown[]) => {
      const result = fn(...args)
      return result instanceof Promise ? trackDataFlight(result) : result
    }
  }
  return wrapped as unknown as ITagClient
}

export function tagClient(): ITagClient {
  if (clientOverride) return clientOverride

  const client: ITagClient = {
    async listTags({ pageSize, pageToken, filter }) {
      const params = new URLSearchParams()
      if (pageSize && pageSize > 0) params.set('page_size', String(pageSize))
      if (pageToken) params.set('page_token', pageToken)
      if (filter) params.set('filter', filter)
      const qs = params.toString()
      const resp = await requestJson<Record<string, unknown>>(`/ontology/tags${qs ? `?${qs}` : ''}`)
      const rows = Array.isArray(resp.tags) ? resp.tags : []
      return {
        tags: rows.map(tagRecordFromJson),
        nextPageToken: str(resp.next_page_token),
        totalSize: num(resp.total_size)
      }
    },
    // Creation goes through POST /ontology/tags/batch with a single item — the
    // batch endpoint is the proxy's only registration surface (it also
    // carries scrape_mode, which the old ConnectRPC CreateTag could not).
    async createTag({ tag, initialScrapeConfig }) {
      const item: Record<string, unknown> = {
        tag_path: tag.tagPath,
        source_url: tag.sourceUrl,
        source_type: tag.sourceType,
        enabled: tag.state !== 'disabled'
      }
      if (tag.scrapeMode) item.scrape_mode = tag.scrapeMode
      if (tag.displayLabel) item.display_label = tag.displayLabel
      if (initialScrapeConfig && initialScrapeConfig.frequencyMs > 0) {
        item.frequency_ms = Math.floor(initialScrapeConfig.frequencyMs)
      }
      const resp = await requestJson<Record<string, unknown>>('/ontology/tags/batch', {
        method: 'POST',
        body: JSON.stringify({ tags: [item] })
      })
      const results = Array.isArray(resp.results) ? resp.results : []
      const first = (results[0] ?? {}) as Record<string, unknown>
      if (first.error && typeof first.error === 'object') {
        const err = first.error as Record<string, unknown>
        throw new Error(str(err.message) || 'tag create failed')
      }
      if (!first.tag || typeof first.tag !== 'object') {
        // Neither a tag nor an error came back — surface it rather than
        // fabricating an all-defaults TagRecord.
        throw new Error('tag create failed: no tag returned')
      }
      return tagRecordFromJson(first.tag)
    },
    async deleteTag({ name }) {
      await requestJson<void>(`/ontology/tags/${encodeURIComponent(tagIdFromName(name))}`, {
        method: 'DELETE'
      })
    },
    async getTagScrapeConfig({ name }) {
      const resp = await requestJson<Record<string, unknown>>(
        `/ontology/tags/${encodeURIComponent(tagIdFromName(name))}/scrape-config`
      )
      return scrapeConfigFromJson(resp)
    },
    async updateTagScrapeConfig({ name, frequencyMs, relayFallbackEnabled, enabled }) {
      const body: Record<string, unknown> = {}
      if (frequencyMs !== undefined) body.frequency_ms = Math.floor(frequencyMs)
      if (relayFallbackEnabled !== undefined) {
        body.relay_fallback_enabled = relayFallbackEnabled
      }
      if (enabled !== undefined) body.enabled = enabled
      const resp = await requestJson<Record<string, unknown>>(
        `/ontology/tags/${encodeURIComponent(tagIdFromName(name))}/scrape-config`,
        {
          method: 'PATCH',
          body: JSON.stringify(body)
        }
      )
      return scrapeConfigFromJson(resp)
    },
    async setTagsEnabled({ tagIds, nodeIds, containerScope, searchQuery, enabled, preview }) {
      const body: Record<string, unknown> = { enabled, preview }
      if (tagIds?.length) body.tag_ids = tagIds
      if (nodeIds?.length) body.node_ids = nodeIds
      if (containerScope) {
        body.container_scope = {
          id: containerScope.id,
          kind: containerScope.kind
        }
      }
      if (searchQuery?.trim()) body.search_query = searchQuery.trim()
      const resp = await requestJson<Record<string, unknown>>('/ontology/tags:setEnabled', {
        method: 'POST',
        body: JSON.stringify(body)
      })
      return {
        preview: resp.preview === true,
        enabled: resp.enabled === true,
        selectedCount: num(resp.selected_count),
        registeredCount: num(resp.registered_count),
        graphOnlyCount: num(resp.graph_only_count),
        updatedCount: num(resp.updated_count),
        resolvedNodeIds: Array.isArray(resp.resolved_node_ids)
          ? resp.resolved_node_ids.filter((id): id is string => typeof id === 'string')
          : []
      }
    },
    async getTagScrapeStatus({ name }) {
      const resp = await requestJson<Record<string, unknown>>(
        `/ontology/tags/${encodeURIComponent(tagIdFromName(name))}/scrape-status`
      )
      return scrapeStatusFromJson(resp)
    },
    async listTagReadings({ parent, pageSize, windowSecs }) {
      const params = new URLSearchParams()
      if (pageSize && pageSize > 0) params.set('limit', String(pageSize))
      if (windowSecs && windowSecs > 0) params.set('window_secs', String(Math.floor(windowSecs)))
      const qs = params.toString()
      const tagName = tagNameFromId(parent)
      const resp = await requestJson<Record<string, unknown>>(
        `/ontology/tags/${encodeURIComponent(tagIdFromName(parent))}/readings${qs ? `?${qs}` : ''}`
      )
      const rows = Array.isArray(resp.readings) ? resp.readings : []
      return {
        readings: rows.map((r) => readingFromJson(tagName, r)),
        // REST keeps the ConnectRPC limit-only pagination: no next token.
        nextPageToken: ''
      }
    },
    async getTagSeries({ parent, windowSecs, bucketSecs }) {
      const params = new URLSearchParams({
        window_secs: String(Math.floor(windowSecs)),
        bucket_secs: String(Math.floor(bucketSecs))
      })
      const resp = await requestJson<Record<string, unknown>>(
        `/ontology/tags/${encodeURIComponent(tagIdFromName(parent))}/series?${params.toString()}`
      )
      const pts = Array.isArray(resp.points) ? resp.points : []
      return {
        tag: tagNameFromId(str(resp.tag_id) || tagIdFromName(parent)),
        points: pts.map(seriesPointFromJson),
        windowSecs: num(resp.window_secs),
        bucketSecs: num(resp.bucket_secs)
      }
    },
    // The statistics surface below was always REST; unchanged by the
    // ConnectRPC deprecation (it is the readings/health backend).
    async getTagSummary({ sourceUrl, tagPath, tagId, windowSecs }) {
      // Preserve the normalized source/path fallback for raw readings.
      // Registered Timescale rows are selected by tagId.
      const gatewayUrl = sourceUrl.trim().replace(/\/+$/, '')
      const params = new URLSearchParams({
        gateway_url: gatewayUrl,
        tag_path: tagPath
      })
      if (tagId) {
        params.set('tag_id', tagId)
      }
      if (windowSecs && windowSecs > 0) {
        params.set('window', `${Math.floor(windowSecs)}s`)
      }
      const resp = await fetch(tagsApiUrl(`/ontology/tags/stats/summary?${params.toString()}`), {
        headers: tagsApiHeaders()
      })
      if (!resp.ok) {
        throw new Error(`tag summary failed: ${resp.status} ${resp.statusText}`)
      }
      return tagSummaryFromJson(await resp.json())
    },
    // batchGetTagSummaries fans out POST /api/v1/ontology/tags/stats/summary/batch:
    // a single request returns one Summary (or per-item error) per
    // selector. The Tags page uses this for its "Problems" filter, which
    // needs a health verdict for every visible tag without N round-trips.
    async batchGetTagSummaries({ items, windowSecs }) {
      if (items.length === 0) {
        return { windowSeconds: 0, items: [] }
      }
      const body: Record<string, unknown> = {
        items: items.map((it) => ({
          gateway_url: it.sourceUrl.trim().replace(/\/+$/, ''),
          tag_path: it.tagPath,
          ...(it.tagId ? { tag_id: it.tagId } : {})
        }))
      }
      if (windowSecs && windowSecs > 0) {
        body.window = `${Math.floor(windowSecs)}s`
      }
      const resp = await fetch(tagsApiUrl('/ontology/tags/stats/summary/batch'), {
        method: 'POST',
        headers: tagsApiHeaders(),
        body: JSON.stringify(body)
      })
      if (!resp.ok) {
        throw new Error(`tag summary batch failed: ${resp.status} ${resp.statusText}`)
      }
      return batchSummariesFromJson(await resp.json())
    },
    async deregisterMtcLiveReadings({ agentUrl }) {
      const resp = await requestJson<Record<string, unknown>>(
        '/ontology/mtc-endpoints:deregisterLiveReadings',
        {
          method: 'POST',
          body: JSON.stringify({ agent_url: agentUrl })
        }
      )
      return {
        tagsDeregistered: num(resp.tags_deregistered),
        endpointDeleted: Boolean(resp.endpoint_deleted)
      }
    },
    async previewOrphanCleanup({ search, page, pageSize, refresh }) {
      const params = new URLSearchParams()
      if (search) params.set('search', search)
      if (page && page > 1) params.set('page', String(page))
      if (pageSize && pageSize > 0) params.set('page_size', String(pageSize))
      if (refresh) params.set('refresh', 'true')
      const qs = params.toString()
      const resp = await requestJson<Record<string, unknown>>(
        `/ontology/tags:orphanCleanupPreview${qs ? `?${qs}` : ''}`
      )
      const rowsRaw = Array.isArray(resp.rows) ? resp.rows : []
      const gatewaysRaw = Array.isArray(resp.gateways) ? resp.gateways : []
      return {
        fingerprint: str(resp.fingerprint),
        totalTags: num(resp.total_tags),
        totalEndpoints: num(resp.total_endpoints),
        totalReadings: num(resp.total_readings),
        totalReadingsCapped: Boolean(resp.total_readings_capped),
        matchedTags: num(resp.matched_tags),
        page: num(resp.page),
        pageSize: num(resp.page_size),
        gateways: gatewaysRaw.map((row) => {
          const j = (row ?? {}) as Record<string, unknown>
          return {
            sourceUrl: str(j.source_url),
            tags: num(j.tags),
            endpointRemoved: Boolean(j.endpoint_removed)
          }
        }),
        rows: rowsRaw.map((row) => {
          const j = (row ?? {}) as Record<string, unknown>
          return {
            tagPath: str(j.tag_path),
            sourceUrl: str(j.source_url)
          }
        })
      }
    },
    // Handled without requestJson because a 409 is not a failure here: it
    // means someone else's run is already doing the work, and its id is in the
    // body so this caller can follow that run instead.
    async startOrphanCleanup({ fingerprint }) {
      const resp = await fetch(tagsApiUrl('/ontology/tags:cleanupOrphans'), {
        method: 'POST',
        headers: tagsApiHeaders(),
        body: JSON.stringify({ fingerprint })
      })
      const body = await readJsonObject(resp)
      if (resp.status === 409) {
        return { jobId: str(body.job_id), alreadyRunning: true }
      }
      if (!resp.ok) throw new Error(responseErrorDetail(resp, body))
      return { jobId: str(body.job_id), alreadyRunning: false }
    },
    async getOrphanCleanupRun({ jobId }) {
      const resp = await requestJson<Record<string, unknown>>(
        `/ontology/jobs/${encodeURIComponent(jobId)}`
      )
      const payload = (resp.payload ?? {}) as Record<string, unknown>
      return {
        jobId: str(resp.id),
        state: str(resp.state),
        error: str(resp.error),
        progress: {
          phase: str(payload.phase) as OrphanCleanupPhase,
          totalTags: num(payload.total_tags),
          pairsDone: num(payload.pairs_done),
          readingsDeleted: num(payload.readings_deleted),
          tagsDeleted: num(payload.tags_deleted),
          endpointsDeleted: num(payload.endpoints_deleted)
        }
      }
    },
    async getOrphanCleanupStatus() {
      const resp = await requestJson<Record<string, unknown>>('/ontology/tags:orphanCleanupStatus')
      return { running: Boolean(resp.running), jobId: str(resp.job_id) }
    },
    async getAcquisitionSummary() {
      const resp = await requestJson<Record<string, unknown>>('/ontology/tags:acquisitionSummary')
      const rows = Array.isArray(resp.source_urls) ? resp.source_urls : []
      const opcEndpoints = Array.isArray(resp.opc_endpoints) ? resp.opc_endpoints : []
      return {
        pollCount: num(resp.poll_count),
        subscribeCount: num(resp.subscribe_count),
        sourceUrls: rows.map((row) => {
          const j = (row ?? {}) as Record<string, unknown>
          return {
            sourceUrl: str(j.source_url),
            sourceType: str(j.source_type),
            scrapeMode: str(j.scrape_mode),
            count: num(j.count)
          }
        }),
        opcEndpoints: opcEndpoints.map((endpoint) => {
          const j = (endpoint ?? {}) as Record<string, unknown>
          return {
            endpointUrl: str(j.endpoint_url),
            hasSavedCredentials: j.has_saved_credentials === true,
            anonymousAccess: j.anonymous_access === true
          }
        })
      }
    },
    async getPollSettings() {
      return toPollSettings(
        await requestJson<Record<string, unknown>>('/ontology/tags:pollSettings')
      )
    },
    async updatePollSettings({ pollFrequencyMs }) {
      return toPollSettings(
        await requestJson<Record<string, unknown>>('/ontology/tags:pollSettings', {
          method: 'PUT',
          body: JSON.stringify({ poll_frequency_ms: pollFrequencyMs })
        })
      )
    },
    async convertAcquisitionMode({
      targetMode,
      httpGatewayUrl,
      opcEndpointUrl,
      connection,
      tagIds,
      nodeIds,
      containerScope,
      searchQuery,
      preview
    }) {
      const body: Record<string, unknown> = {
        target_mode: targetMode,
        http_gateway_url: httpGatewayUrl,
        opc_endpoint_url: opcEndpointUrl
      }
      if (tagIds?.length) body.tag_ids = tagIds
      if (nodeIds?.length) body.node_ids = nodeIds
      if (containerScope) {
        body.container_scope = {
          id: containerScope.id,
          kind: containerScope.kind
        }
      }
      if (searchQuery?.trim()) body.search_query = searchQuery.trim()
      if (preview !== undefined) body.preview = preview
      if (connection) {
        const conn: Record<string, unknown> = {
          endpoint_url: connection.endpointUrl ?? opcEndpointUrl,
          anonymous_access: connection.anonymousAccess ?? !connection.username
        }
        if (connection.securityPolicies) conn.security_policies = connection.securityPolicies
        if (connection.usernameSource) conn.username_source = connection.usernameSource
        if (connection.username) conn.username = connection.username
        if (connection.password) conn.password = connection.password
        body.connection = conn
      }
      const resp = await requestJson<Record<string, unknown>>(
        '/ontology/tags:convertAcquisitionMode',
        {
          method: 'POST',
          body: JSON.stringify(body)
        }
      )
      const failed = Array.isArray(resp.failed) ? resp.failed : []
      const skippedDetails = Array.isArray(resp.skipped_details) ? resp.skipped_details : []
      return {
        converted: num(resp.converted),
        skipped: num(resp.skipped),
        skippedDetails: skippedDetails.map((row) => {
          const j = (row ?? {}) as Record<string, unknown>
          const nodeId = nullableStr(j.node_id)
          return {
            tagId: str(j.tag_id),
            tagPath: str(j.tag_path),
            reason: str(j.reason),
            ...(nodeId ? { nodeId } : {})
          }
        }),
        failed: failed.map((row) => {
          const j = (row ?? {}) as Record<string, unknown>
          return {
            tagId: str(j.tag_id),
            tagPath: str(j.tag_path),
            reason: str(j.reason)
          }
        })
      }
    }
  }
  return withDataFlightTracking(client)
}

// ---------------------------------------------------------------------------
// Statistics summary JSON mappers (snake_case wire -> camelCase records)
// ---------------------------------------------------------------------------

function numericSummaryFromJson(j: Record<string, unknown> | undefined): TagNumericSummary | null {
  if (!j) return null
  return {
    windowSeconds: num(j.window_seconds),
    count: num(j.count),
    min: nullableNum(j.min),
    max: nullableNum(j.max),
    range: nullableNum(j.range),
    mean: nullableNum(j.mean),
    stddev: nullableNum(j.stddev),
    coefficientOfVariation: nullableNum(j.coefficient_of_variation),
    median: nullableNum(j.median),
    p5: nullableNum(j.p5),
    p25: nullableNum(j.p25),
    p75: nullableNum(j.p75),
    p95: nullableNum(j.p95),
    p99: nullableNum(j.p99),
    iqr: nullableNum(j.iqr),
    timeWeightedAverage: nullableNum(j.time_weighted_average),
    lastValue: nullableNum(j.last_value),
    lastTime: nullableStr(j.last_time),
    lastZScore: nullableNum(j.last_z_score),
    lastWithin1Sigma: j.last_within_1_sigma === true,
    lastWithin2Sigma: j.last_within_2_sigma === true,
    lastWithin3Sigma: j.last_within_3_sigma === true
  }
}

function stateSummaryFromJson(j: Record<string, unknown> | undefined): TagStateSummary | null {
  if (!j) return null
  const rawStates = Array.isArray(j.states) ? (j.states as Record<string, unknown>[]) : []
  return {
    windowSeconds: num(j.window_seconds),
    count: num(j.count),
    badQualitySamples: num(j.bad_quality_samples),
    states: rawStates.map((s) => ({
      state: typeof s.state === 'string' ? s.state : '',
      sampleCount: num(s.sample_count),
      samplePercent: num(s.sample_percent),
      secondsInState: num(s.seconds_in_state),
      timeWeightedPercent: num(s.time_weighted_percent)
    })),
    currentState: nullableStr(j.current_state),
    currentStateSince: nullableStr(j.current_state_since),
    currentDurationSeconds: nullableNum(j.current_duration_seconds),
    transitionCount: num(j.transition_count)
  }
}

function dataQualityFromJson(j: Record<string, unknown> | undefined): TagDataQuality {
  const q = j ?? {}
  return {
    windowSeconds: num(q.window_seconds),
    sampleCount: num(q.sample_count),
    goodCount: num(q.good_count),
    badCount: num(q.bad_count),
    goodPercent: num(q.good_percent),
    samplesPerMinute: num(q.samples_per_minute),
    firstReadingAt: nullableStr(q.first_reading_at),
    lastReadingAt: nullableStr(q.last_reading_at),
    lastReadingIsGood: nullableBool(q.last_reading_is_good),
    stalenessSeconds: nullableNum(q.staleness_seconds),
    longestGapSeconds: nullableNum(q.longest_gap_seconds),
    meanGapSeconds: nullableNum(q.mean_gap_seconds)
  }
}

function tagSummaryFromJson(raw: unknown): TagSummaryRecord {
  const j = (raw ?? {}) as Record<string, unknown>
  return {
    class: (j.class as TagClassName) ?? 'other',
    classificationReason:
      typeof j.classification_reason === 'string' ? j.classification_reason : '',
    windowSeconds: num(j.window_seconds),
    numeric: numericSummaryFromJson(j.numeric as Record<string, unknown> | undefined),
    state: stateSummaryFromJson(j.state as Record<string, unknown> | undefined),
    dataQuality: dataQualityFromJson(j.data_quality as Record<string, unknown> | undefined)
  }
}

function batchSummariesFromJson(raw: unknown): BatchGetTagSummariesResult {
  const j = (raw ?? {}) as Record<string, unknown>
  const rawItems = Array.isArray(j.items) ? (j.items as Record<string, unknown>[]) : []
  return {
    windowSeconds: num(j.window_seconds),
    items: rawItems.map((it) => ({
      sourceUrl: typeof it.gateway_url === 'string' ? it.gateway_url : '',
      tagPath: typeof it.tag_path === 'string' ? it.tag_path : '',
      summary: it.summary && typeof it.summary === 'object' ? tagSummaryFromJson(it.summary) : null,
      error: typeof it.error === 'string' && it.error.length > 0 ? it.error : null
    }))
  }
}

// ---------------------------------------------------------------------------
// Error helpers
// ---------------------------------------------------------------------------

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------
//
// These are lightweight hand-written hooks rather than RTK Query endpoints.
// Mutations bump a local refresh counter so the corresponding list/queries
// can re-fetch. Names and signatures are stable from the ConnectRPC era.

interface QueryResult<T> {
  data: T | undefined
  isLoading: boolean
  error: string | undefined
  refetch: () => void
}

interface MutationState {
  isLoading: boolean
  error: string | undefined
}

type MutationFn<TArg, TResult> = (arg: TArg) => Promise<TResult>

type TagAcquisitionQueryKind =
  | 'registry'
  | 'scrape-config'
  | 'scrape-status'
  | 'health'
  | 'poll-settings'

const tagAcquisitionQueryListeners: Record<TagAcquisitionQueryKind, Set<() => void>> = {
  registry: new Set(),
  'scrape-config': new Set(),
  'scrape-status': new Set(),
  health: new Set(),
  'poll-settings': new Set()
}

/**
 * Invalidates every mounted acquisition query after registration changes.
 * Queries that mount later fetch fresh data normally; mounted consumers
 * subscribe by category so mutation controls do not need hidden query hooks.
 */
export function invalidateTagAcquisitionQueries(): void {
  // Inventory pages render acquisition state alongside graph rows, so any
  // write that changes acquisition also invalidates the cached inventory.
  invalidateTagInventoryQueryCache()
  for (const listeners of Object.values(tagAcquisitionQueryListeners)) {
    for (const listener of listeners) listener()
  }
}

function useTagAcquisitionInvalidationTick(kind?: TagAcquisitionQueryKind): number {
  const [tick, setTick] = useState(0)
  useEffect(() => {
    if (!kind) return
    const listener = () => setTick((value) => value + 1)
    tagAcquisitionQueryListeners[kind].add(listener)
    return () => {
      tagAcquisitionQueryListeners[kind].delete(listener)
    }
  }, [kind])
  return tick
}

function useQuery<TArg, TResult>(
  arg: TArg,
  fetcher: (arg: TArg, signal: AbortSignal) => Promise<TResult>,
  argKey: string,
  invalidationKind?: TagAcquisitionQueryKind,
  opts: { skip?: boolean } = {}
): QueryResult<TResult> {
  const [data, setData] = useState<TResult | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | undefined>(undefined)
  const [refreshTick, setRefreshTick] = useState(0)
  const invalidationTick = useTagAcquisitionInvalidationTick(invalidationKind)
  const latestRequestId = useRef(0)

  // Stash the latest arg + fetcher in refs so we can omit them from the
  // effect's dep array without lying to the linter. The effect re-runs only
  // when `argKey` (the stable content hash) or `refreshTick` changes;
  // identity churn on every render no longer causes a refetch storm.
  const argRef = useRef(arg)
  const fetcherRef = useRef(fetcher)
  argRef.current = arg
  fetcherRef.current = fetcher

  useEffect(() => {
    if (opts.skip) {
      setData(undefined)
      setIsLoading(false)
      setError(undefined)
      return
    }
    const ac = new AbortController()
    const requestId = ++latestRequestId.current
    setIsLoading(true)
    setError(undefined)
    fetcherRef
      .current(argRef.current, ac.signal)
      .then((value) => {
        if (requestId !== latestRequestId.current) return
        setData(value)
        setIsLoading(false)
      })
      .catch((err) => {
        if (requestId !== latestRequestId.current) return
        if (ac.signal.aborted) return
        setError(errorMessage(err))
        setIsLoading(false)
      })
    return () => {
      ac.abort()
    }
  }, [argKey, refreshTick, invalidationTick, opts.skip])

  const refetch = useCallback(() => setRefreshTick((n) => n + 1), [])

  return { data, isLoading, error, refetch }
}

function useMutation<TArg, TResult>(
  fn: MutationFn<TArg, TResult>
): [(arg: TArg) => Promise<TResult>, MutationState] {
  const [state, setState] = useState<MutationState>({
    isLoading: false,
    error: undefined
  })
  const trigger = useCallback(
    async (arg: TArg) => {
      setState({ isLoading: true, error: undefined })
      try {
        const result = await fn(arg)
        setState({ isLoading: false, error: undefined })
        return result
      } catch (err) {
        const msg = errorMessage(err)
        setState({ isLoading: false, error: msg })
        throw err
      }
    },
    [fn]
  )
  return [trigger, state]
}

// --- queries ---------------------------------------------------------------

export function useListTagsQuery(args: ListTagsArgs = {}): QueryResult<ListTagsResult> {
  const key = JSON.stringify(args)
  return useQuery<ListTagsArgs, ListTagsResult>(
    args,
    (a) => tagClient().listTags(a),
    key,
    'registry'
  )
}

export function useGetTagScrapeConfigQuery(
  args: { name: string },
  opts: { skip?: boolean } = {}
): QueryResult<TagScrapeConfigRecord> {
  const key = `${args.name}|${opts.skip ? 'skip' : 'load'}`
  return useQuery<{ name: string }, TagScrapeConfigRecord>(
    args,
    (a) => tagClient().getTagScrapeConfig(a),
    key,
    'scrape-config',
    opts
  )
}

export function useGetTagScrapeStatusQuery(
  args: { name: string },
  opts: { skip?: boolean } = {}
): QueryResult<TagScrapeStatusRecord> {
  const key = `${args.name}|${opts.skip ? 'skip' : 'load'}`
  return useQuery<{ name: string }, TagScrapeStatusRecord>(
    args,
    (a) => tagClient().getTagScrapeStatus(a),
    key,
    'scrape-status',
    opts
  )
}

export function useListTagReadingsQuery(
  args: ListTagReadingsArgs
): QueryResult<ListTagReadingsResult> {
  const key = JSON.stringify(args)
  return useQuery<ListTagReadingsArgs, ListTagReadingsResult>(
    args,
    (a) => tagClient().listTagReadings(a),
    key
  )
}

export function useGetTagSeriesQuery(args: GetTagSeriesArgs): QueryResult<GetTagSeriesResult> {
  const key = JSON.stringify(args)
  return useQuery<GetTagSeriesArgs, GetTagSeriesResult>(
    args,
    (a) => tagClient().getTagSeries(a),
    key
  )
}

export function useTagSummaryQuery(
  args: TagSummaryArgs,
  opts: { skip?: boolean } = {}
): QueryResult<TagSummaryRecord> {
  const skip = opts.skip === true
  const key = JSON.stringify({ args, skip })
  const result = useQuery<TagSummaryArgs, TagSummaryRecord | undefined>(
    args,
    (a) => (skip ? Promise.resolve(undefined) : tagClient().getTagSummary(a)),
    key,
    'health'
  )
  return result as QueryResult<TagSummaryRecord>
}

// Chunked batch summary hook. The proxy caps a single batch at
// SummaryBatchMaxItems (500) on the server; this hook chunks the input,
// issues requests in parallel, and merges per-chunk responses into one map
// keyed by tagPath. Partial results surface as chunks land so callers (the
// Tags page Problems filter, the row health chips) can render progressively
// for large folders.
const SUMMARY_BATCH_CHUNK = 200

export interface BatchTagSummariesQueryResult {
  /** Per-normalized-(sourceUrl, tagPath) summary map. */
  data: Map<string, TagSummaryRecord>
  /** Per-normalized-(sourceUrl, tagPath) error map. */
  errors: Map<string, string>
  /** True until every chunk has settled. Partial maps are usable. */
  isLoading: boolean
  /** Aggregate top-level error if every chunk failed. */
  error: string | undefined
  refetch: () => void
}

export function tagSummaryIdentityKey(sourceUrl: string, tagPath: string): string {
  return `${sourceUrl.trim().replace(/\/+$/, '')}\u0000${tagPath}`
}

export function useBatchTagSummariesQuery(
  args: BatchGetTagSummariesArgs
): BatchTagSummariesQueryResult {
  const [data, setData] = useState<Map<string, TagSummaryRecord>>(new Map())
  const [errors, setErrors] = useState<Map<string, string>>(new Map())
  const [isLoading, setIsLoading] = useState(args.items.length > 0)
  const [error, setError] = useState<string | undefined>(undefined)
  const [refreshTick, setRefreshTick] = useState(0)
  const invalidationTick = useTagAcquisitionInvalidationTick('health')
  const latestRequestId = useRef(0)

  // Key on the sorted physical identities so re-mounting with the same selection
  // doesn't refetch; explicit windowSecs change does.
  const key = JSON.stringify({
    identities: args.items
      .map((i) => `${i.tagId?.trim() ?? ''}\u0000${tagSummaryIdentityKey(i.sourceUrl, i.tagPath)}`)
      .sort(),
    windowSecs: args.windowSecs
  })
  const itemsRef = useRef(args.items)
  const windowRef = useRef(args.windowSecs)
  itemsRef.current = args.items
  windowRef.current = args.windowSecs

  useEffect(() => {
    const items = itemsRef.current
    if (items.length === 0) {
      setData(new Map())
      setErrors(new Map())
      setIsLoading(false)
      setError(undefined)
      return
    }
    const requestId = ++latestRequestId.current
    setData(new Map())
    setErrors(new Map())
    setIsLoading(true)
    setError(undefined)

    const chunks: TagSummaryBatchItem[][] = []
    for (let i = 0; i < items.length; i += SUMMARY_BATCH_CHUNK) {
      chunks.push(items.slice(i, i + SUMMARY_BATCH_CHUNK))
    }

    const client = tagClient()
    let failedChunks = 0
    const promises = chunks.map((chunk) =>
      client
        .batchGetTagSummaries({ items: chunk, windowSecs: windowRef.current })
        .then((result) => {
          if (requestId !== latestRequestId.current) return
          // Merge into the running maps progressively. Each chunk is a
          // few hundred entries; spread + new Map is cheap.
          setData((prev) => {
            const next = new Map(prev)
            for (const it of result.items) {
              if (it.summary) {
                next.set(tagSummaryIdentityKey(it.sourceUrl, it.tagPath), it.summary)
              }
            }
            return next
          })
          setErrors((prev) => {
            const next = new Map(prev)
            for (const it of result.items) {
              if (it.error) {
                next.set(tagSummaryIdentityKey(it.sourceUrl, it.tagPath), it.error)
              }
            }
            return next
          })
        })
        .catch((err) => {
          if (requestId !== latestRequestId.current) return
          failedChunks += 1
          setErrors((prev) => {
            const next = new Map(prev)
            for (const item of chunk) {
              next.set(tagSummaryIdentityKey(item.sourceUrl, item.tagPath), errorMessage(err))
            }
            return next
          })
        })
    )

    void Promise.allSettled(promises).then(() => {
      if (requestId !== latestRequestId.current) return
      setIsLoading(false)
      // Surface a top-level error only when *every* chunk failed so a
      // single hiccup doesn't blank the filter UI.
      if (failedChunks === chunks.length && chunks.length > 0) {
        setError('Failed to load tag health')
      }
    })
  }, [key, refreshTick, invalidationTick])

  const refetch = useCallback(() => setRefreshTick((n) => n + 1), [])
  return { data, errors, isLoading, error, refetch }
}

// --- mutations -------------------------------------------------------------

export function useCreateTagMutation(): [
  (arg: CreateTagArgs) => Promise<TagRecord>,
  MutationState
] {
  return useMutation<CreateTagArgs, TagRecord>(async (arg) => {
    const result = await tagClient().createTag(arg)
    invalidateTagAcquisitionQueries()
    return result
  })
}

export function useDeleteTagMutation(): [(arg: { name: string }) => Promise<void>, MutationState] {
  return useMutation<{ name: string }, void>(async (arg) => {
    await tagClient().deleteTag(arg)
    invalidateTagAcquisitionQueries()
  })
}

export function useUpdateTagScrapeConfigMutation(): [
  (arg: UpdateTagScrapeConfigArgs) => Promise<TagScrapeConfigRecord>,
  MutationState
] {
  return useMutation<UpdateTagScrapeConfigArgs, TagScrapeConfigRecord>(async (arg) => {
    const result = await tagClient().updateTagScrapeConfig(arg)
    invalidateTagAcquisitionQueries()
    return result
  })
}

export function useSetTagsEnabledMutation(): [
  (arg: SetTagsEnabledArgs) => Promise<SetTagsEnabledResult>,
  MutationState
] {
  return useMutation<SetTagsEnabledArgs, SetTagsEnabledResult>(async (arg) => {
    const result = arg.tagIds?.length
      ? await tagClient().setTagsEnabled(arg)
      : await setTagsEnabledBySelection(arg)
    if (!arg.preview) invalidateTagAcquisitionQueries()
    return result
  })
}

export function useDeleteTagsMutation(): [
  (arg: DeleteTagsArgs) => Promise<DeleteTagsResult>,
  MutationState
] {
  return useMutation<DeleteTagsArgs, DeleteTagsResult>(async (arg) => {
    const result = await deleteTagsBySelection(arg)
    if (!arg.preview) invalidateTagAcquisitionQueries()
    return result
  })
}

export function useDeregisterMtcLiveReadingsMutation(): [
  (arg: DeregisterMtcLiveReadingsArgs) => Promise<DeregisterMtcLiveReadingsResult>,
  MutationState
] {
  return useMutation<DeregisterMtcLiveReadingsArgs, DeregisterMtcLiveReadingsResult>(
    async (arg) => {
      const result = await tagClient().deregisterMtcLiveReadings(arg)
      invalidateTagAcquisitionQueries()
      return result
    }
  )
}

export function useOrphanCleanupPreviewQuery(
  arg: OrphanCleanupPreviewArgs,
  opts: { skip?: boolean } = {}
): QueryResult<OrphanCleanupPreview> {
  return useQuery<OrphanCleanupPreviewArgs, OrphanCleanupPreview>(
    arg,
    (a) => tagClient().previewOrphanCleanup(a),
    `${arg.search ?? ''}|${arg.page ?? 1}|${arg.pageSize ?? 0}|${arg.refresh ? 1 : 0}`,
    undefined,
    opts
  )
}

/**
 * Hands the delete to a background run. Resolves as soon as the run exists —
 * clearing a fleet's history takes minutes, so the caller follows the run with
 * useOrphanCleanupRunQuery rather than awaiting the work itself.
 */
export function useStartOrphanCleanupMutation(): [
  (arg: CleanupOrphanTagsArgs) => Promise<StartOrphanCleanupResult>,
  MutationState
] {
  return useMutation<CleanupOrphanTagsArgs, StartOrphanCleanupResult>((arg) =>
    tagClient().startOrphanCleanup(arg)
  )
}

/** Reports a cleanup already under way, so a caller can attach to it. */
export function useOrphanCleanupStatusQuery(
  opts: { skip?: boolean } = {}
): QueryResult<OrphanCleanupStatus> {
  return useQuery<undefined, OrphanCleanupStatus>(
    undefined,
    () => tagClient().getOrphanCleanupStatus(),
    'orphan-cleanup-status',
    undefined,
    opts
  )
}

/** How often a running cleanup is re-read. */
const ORPHAN_CLEANUP_POLL_MS = 1500

/**
 * Follows a cleanup run until it terminalizes, then stops polling. Registry
 * queries are invalidated once on completion, because that is when the tags
 * the rest of the UI is showing actually disappear.
 *
 * Pass null for jobId when no run is being followed.
 */
export function useOrphanCleanupRunQuery(jobId: string | null): {
  run: OrphanCleanupRun | undefined
  error: string | undefined
} {
  const [run, setRun] = useState<OrphanCleanupRun | undefined>(undefined)
  const [error, setError] = useState<string | undefined>(undefined)

  useEffect(() => {
    setRun(undefined)
    setError(undefined)
    if (!jobId) return

    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const poll = async () => {
      try {
        const next = await tagClient().getOrphanCleanupRun({ jobId })
        if (cancelled) return
        setRun(next)
        if (next.state === 'done' || next.state === 'error') {
          invalidateTagAcquisitionQueries()
          return
        }
      } catch (err) {
        if (cancelled) return
        // A transient read failure says nothing about the run, which is
        // committing batches server-side regardless. Surface it and keep
        // watching.
        setError(errorMessage(err))
      }
      if (!cancelled) timer = setTimeout(() => void poll(), ORPHAN_CLEANUP_POLL_MS)
    }
    void poll()

    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [jobId])

  return { run, error }
}

export function useAcquisitionSummaryQuery(): QueryResult<AcquisitionSummary> {
  return useQuery<undefined, AcquisitionSummary>(
    undefined,
    () => tagClient().getAcquisitionSummary(),
    'acquisition-summary',
    'registry'
  )
}

export function useTagPollSettingsQuery(): QueryResult<TagPollSettings> {
  return useQuery<undefined, TagPollSettings>(
    undefined,
    () => tagClient().getPollSettings(),
    'poll-settings',
    'poll-settings'
  )
}

export function useUpdateTagPollSettingsMutation(): [
  (arg: { pollFrequencyMs: number }) => Promise<TagPollSettings>,
  MutationState
] {
  return useMutation<{ pollFrequencyMs: number }, TagPollSettings>(async (arg) => {
    const result = await tagClient().updatePollSettings(arg)
    for (const listener of tagAcquisitionQueryListeners['poll-settings']) listener()
    return result
  })
}

export function useConvertAcquisitionModeMutation(): [
  (arg: ConvertAcquisitionModeArgs) => Promise<ConvertAcquisitionModeResult>,
  MutationState
] {
  return useMutation<ConvertAcquisitionModeArgs, ConvertAcquisitionModeResult>(async (arg) => {
    const result = await tagClient().convertAcquisitionMode(arg)
    if (arg.preview !== true) invalidateTagAcquisitionQueries()
    return result
  })
}

// ---------------------------------------------------------------------------
// Manual db data-point registration ("register this query as a data point")
// ---------------------------------------------------------------------------
//
// Unlike the proxy-backed tag methods in this file, this goes through the
// api-service BFF route
// `POST /api/ontology/db-datapoints:register`. Registering a `db://` data point
// is a TWO-write operation — the Postgres registry (`POST /ontology/tags/batch` with the
// `db` block) AND the deterministic graph `:Tag` MERGE — orchestrated
// server-side so the browser never duplicates the graph write. The flat form
// (`connection`, `name`, `query`, `value_column`, `cadence`) is normalized and
// validated by the shared `@atbash-labs/ontology-db` builder on the server.
// Serves the DB workbench "save as data point" flow and the config-builder
// register step.

export interface RegisterDbDatapointConnection {
  url: string
  name: string
  transport?: DbTransport
  databaseType?: string
  username?: string
  password?: string
}

export interface RegisterDbDatapointArgs {
  connection: RegisterDbDatapointConnection
  /** Display name for the data point; also the default `db://` datapoint slug. */
  name: string
  /** The read-only SELECT the data point runs. */
  query: string
  valueColumn?: string
  timeColumn?: string
  cadenceMs?: number
  syncMode?: DbSyncMode
  /** Explicit datapoint segment (defaults to a slug of `name`). */
  datapoint?: string
  enabled?: boolean
}

export interface RegisterDbDatapointResult {
  attempted: number
  registered: number
  alreadyExisted: number
  failed: number
  graphWritten: boolean
  errors: Array<{ tagPath: string; reason: string }>
}

const EMPTY_REGISTER_RESULT: RegisterDbDatapointResult = {
  attempted: 0,
  registered: 0,
  alreadyExisted: 0,
  failed: 0,
  graphWritten: false,
  errors: []
}

export async function registerDbDatapoint(
  args: RegisterDbDatapointArgs
): Promise<RegisterDbDatapointResult> {
  const { connection } = args
  const body: Record<string, unknown> = {
    connection: {
      url: connection.url,
      name: connection.name,
      ...(connection.transport ? { transport: connection.transport } : {}),
      ...(connection.databaseType ? { database_type: connection.databaseType } : {}),
      ...(connection.username ? { username: connection.username } : {}),
      ...(connection.password ? { password: connection.password } : {})
    },
    name: args.name,
    query: args.query,
    ...(args.valueColumn ? { value_column: args.valueColumn } : {}),
    ...(args.timeColumn ? { time_column: args.timeColumn } : {}),
    ...(args.cadenceMs != null ? { cadence_ms: args.cadenceMs } : {}),
    ...(args.syncMode ? { sync_mode: args.syncMode } : {}),
    ...(args.datapoint ? { datapoint: args.datapoint } : {}),
    ...(args.enabled != null ? { enabled: args.enabled } : {})
  }
  const resp = await fetch(`${getOntologyServiceBaseUrl()}/db-datapoints:register`, {
    method: 'POST',
    headers: createOntologyHeaders(),
    body: JSON.stringify(body)
  })
  const json = (await resp.json().catch(() => ({}))) as {
    result?: RegisterDbDatapointResult
    error?: string
  }
  if (!resp.ok) {
    throw new Error(
      json.error || `db data-point registration failed: ${resp.status} ${resp.statusText}`
    )
  }
  return json.result ?? EMPTY_REGISTER_RESULT
}

export interface DeleteTagsArgs {
  nodeIds?: string[]
  containerScope?: TagContainerScope
  searchQuery?: string
  preview: boolean
}

export interface DeleteTagsResult {
  preview: boolean
  selectedCount: number
  registeredCount: number
  /** Graph tags removed. */
  deletedCount: number
  /** History registrations removed with them. */
  deregisteredCount: number
  resolvedNodeIds: string[]
}

function selectionBody(args: {
  nodeIds?: string[]
  containerScope?: TagContainerScope
  searchQuery?: string
  preview: boolean
}): Record<string, unknown> {
  const body: Record<string, unknown> = { preview: args.preview }
  if (args.nodeIds?.length) body.node_ids = args.nodeIds
  if (args.containerScope) {
    body.container_scope = { id: args.containerScope.id, kind: args.containerScope.kind }
  }
  if (args.searchQuery?.trim()) body.search_query = args.searchQuery.trim()
  return body
}

function resolvedNodeIdsFrom(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : []
}

/** The api-service chunks these past the proxy's 500-tag per-call limit. */
async function postTagBulkAction(
  path: string,
  body: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const resp = await fetch(`${getOntologyServiceBaseUrl()}${path}`, {
    method: 'POST',
    headers: createOntologyHeaders(),
    body: JSON.stringify(body)
  })
  const json = await readJsonObject(resp)
  if (!resp.ok) throw new Error(responseErrorDetail(resp, json))
  return json
}

export async function setTagsEnabledBySelection(
  args: SetTagsEnabledArgs
): Promise<SetTagsEnabledResult> {
  const resp = await postTagBulkAction('/tags/set-enabled', {
    ...selectionBody(args),
    enabled: args.enabled
  })
  return {
    preview: resp.preview === true,
    enabled: resp.enabled === true,
    selectedCount: num(resp.selected_count),
    registeredCount: num(resp.registered_count),
    graphOnlyCount: num(resp.graph_only_count),
    updatedCount: num(resp.updated_count),
    resolvedNodeIds: resolvedNodeIdsFrom(resp.resolved_node_ids)
  }
}

export async function deleteTagsBySelection(args: DeleteTagsArgs): Promise<DeleteTagsResult> {
  const resp = await postTagBulkAction('/tags/delete', selectionBody(args))
  return {
    preview: resp.preview === true,
    selectedCount: num(resp.selected_count),
    registeredCount: num(resp.registered_count),
    deletedCount: num(resp.deleted_count),
    deregisteredCount: num(resp.deregistered_count),
    resolvedNodeIds: resolvedNodeIdsFrom(resp.resolved_node_ids)
  }
}

export function useRegisterDbDatapointMutation(): [
  (arg: RegisterDbDatapointArgs) => Promise<RegisterDbDatapointResult>,
  MutationState
] {
  return useMutation<RegisterDbDatapointArgs, RegisterDbDatapointResult>(registerDbDatapoint)
}

// ---------------------------------------------------------------------------
// Registered db connections ("connect once" — AXI-4467)
// ---------------------------------------------------------------------------
//
// The DB workbench registers a connection ONCE — its password (direct
// transport) is written to the proxy's KMS secret store server-side — then
// addresses schema/query by graph `:Database` URN (`connectionId`). These go
// through the api-service BFF routes `GET/POST /api/ontology/db-connections`,
// which pass through to the proxy `GET/POST /api/v1/ontology/db/connections`. The
// password is never returned: the list/register responses carry only a
// `hasPassword` flag.

export interface DbConnectionSummary {
  /** Graph `:Database` URN — wire `connection_id` for `/db/connections/{id}/*`. */
  connectionId: string
  url: string
  name?: string
  transport: string
  databaseType?: string
  username?: string
  hasPassword: boolean
}

export interface RegisterDbConnectionArgs {
  url: string
  name?: string
  /**
   * Graph `:Database` URN stamped as the registry key (`database_id`).
   * Required for Plant workbench so posture matches `PlantDatabase.id`.
   */
  databaseId?: string
  transport?: DbTransport
  databaseType?: string
  username?: string
  /** Sent once at connect time; stored in the proxy KMS secret store. */
  password?: string
}

interface DbConnectionWire {
  connection_id?: string
  url?: string
  name?: string
  transport?: string
  database_type?: string
  username?: string
  has_password?: boolean
}

function mapDbConnection(w: DbConnectionWire): DbConnectionSummary {
  return {
    connectionId: w.connection_id ?? '',
    url: w.url ?? '',
    ...(w.name ? { name: w.name } : {}),
    transport: w.transport ?? 'webdev',
    ...(w.database_type ? { databaseType: w.database_type } : {}),
    ...(w.username ? { username: w.username } : {}),
    hasPassword: w.has_password === true
  }
}

export async function listDbConnections(signal?: AbortSignal): Promise<DbConnectionSummary[]> {
  const resp = await fetch(`${getOntologyServiceBaseUrl()}/db-connections`, {
    method: 'GET',
    headers: createOntologyHeaders(),
    ...(signal ? { signal } : {})
  })
  const json = (await resp.json().catch(() => ({}))) as {
    connections?: DbConnectionWire[]
    error?: string
  }
  if (!resp.ok) {
    throw new Error(json.error || `list db connections failed: ${resp.status} ${resp.statusText}`)
  }
  return (json.connections ?? []).map(mapDbConnection)
}

export function useListDbConnectionsQuery(): QueryResult<DbConnectionSummary[]> {
  return useQuery<undefined, DbConnectionSummary[]>(
    undefined,
    (_arg, signal) => listDbConnections(signal),
    'db-connections',
    'registry'
  )
}

export async function registerDbConnection(
  args: RegisterDbConnectionArgs
): Promise<DbConnectionSummary> {
  const body: Record<string, unknown> = {
    url: args.url,
    ...(args.name ? { name: args.name } : {}),
    ...(args.databaseId ? { database_id: args.databaseId } : {}),
    ...(args.transport ? { transport: args.transport } : {}),
    ...(args.databaseType ? { database_type: args.databaseType } : {}),
    ...(args.username ? { username: args.username } : {}),
    ...(args.password ? { password: args.password } : {})
  }
  const resp = await fetch(`${getOntologyServiceBaseUrl()}/db-connections`, {
    method: 'POST',
    headers: createOntologyHeaders(),
    body: JSON.stringify(body)
  })
  const json = (await resp.json().catch(() => ({}))) as {
    connection?: DbConnectionWire
    error?: string
  }
  if (!resp.ok) {
    throw new Error(
      json.error || `register db connection failed: ${resp.status} ${resp.statusText}`
    )
  }
  if (!json.connection) {
    throw new Error('register db connection returned no connection')
  }
  const conn = mapDbConnection(json.connection)
  // Refresh any open connection list so the workbench flips to "connected".
  invalidateTagAcquisitionQueries()
  return conn
}

export function useRegisterDbConnectionMutation(): [
  (arg: RegisterDbConnectionArgs) => Promise<DbConnectionSummary>,
  MutationState
] {
  return useMutation<RegisterDbConnectionArgs, DbConnectionSummary>(registerDbConnection)
}

// ---------------------------------------------------------------------------
// WebDev gateway credentials (Basic auth for /system/webdev/*)
// ---------------------------------------------------------------------------

export interface WebDevEndpointSummary {
  gatewayUrl: string
  username?: string
  hasPassword: boolean
}

export interface RegisterWebDevEndpointArgs {
  gatewayUrl: string
  username?: string
  /** Sent once; stored in the proxy secret store. Omit to keep the existing password. */
  password?: string
}

interface WebDevEndpointWire {
  gateway_url?: string
  username?: string
  has_password?: boolean
}

function mapWebDevEndpoint(w: WebDevEndpointWire): WebDevEndpointSummary {
  return {
    gatewayUrl: w.gateway_url ?? '',
    ...(w.username ? { username: w.username } : {}),
    hasPassword: w.has_password === true
  }
}

export async function listWebDevEndpoints(signal?: AbortSignal): Promise<WebDevEndpointSummary[]> {
  const resp = await fetch(`${getOntologyServiceBaseUrl()}/webdev-endpoints`, {
    method: 'GET',
    headers: createOntologyHeaders(),
    ...(signal ? { signal } : {})
  })
  const json = (await resp.json().catch(() => ({}))) as {
    endpoints?: WebDevEndpointWire[]
    error?: string
  }
  if (!resp.ok) {
    throw new Error(json.error || `list webdev endpoints failed: ${resp.status} ${resp.statusText}`)
  }
  return (json.endpoints ?? []).map(mapWebDevEndpoint)
}

export function useListWebDevEndpointsQuery(): QueryResult<WebDevEndpointSummary[]> {
  return useQuery<undefined, WebDevEndpointSummary[]>(
    undefined,
    (_arg, signal) => listWebDevEndpoints(signal),
    'webdev-endpoints',
    'registry'
  )
}

export async function registerWebDevEndpoint(
  args: RegisterWebDevEndpointArgs
): Promise<WebDevEndpointSummary> {
  const body: Record<string, unknown> = {
    gateway_url: args.gatewayUrl,
    ...(args.username ? { username: args.username } : {}),
    ...(args.password ? { password: args.password } : {})
  }
  const resp = await fetch(`${getOntologyServiceBaseUrl()}/webdev-endpoints`, {
    method: 'POST',
    headers: createOntologyHeaders(),
    body: JSON.stringify(body)
  })
  const json = (await resp.json().catch(() => ({}))) as {
    endpoint?: WebDevEndpointWire
    error?: string
  }
  if (!resp.ok) {
    throw new Error(
      json.error || `register webdev endpoint failed: ${resp.status} ${resp.statusText}`
    )
  }
  if (!json.endpoint) {
    throw new Error('register webdev endpoint returned no endpoint')
  }
  invalidateTagAcquisitionQueries()
  return mapWebDevEndpoint(json.endpoint)
}

export function useRegisterWebDevEndpointMutation(): [
  (arg: RegisterWebDevEndpointArgs) => Promise<WebDevEndpointSummary>,
  MutationState
] {
  return useMutation<RegisterWebDevEndpointArgs, WebDevEndpointSummary>(registerWebDevEndpoint)
}

export async function deleteWebDevEndpoint(gatewayUrl: string): Promise<void> {
  const resp = await fetch(`${getOntologyServiceBaseUrl()}/webdev-endpoints:delete`, {
    method: 'POST',
    headers: createOntologyHeaders(),
    body: JSON.stringify({ gateway_url: gatewayUrl })
  })
  const json = (await resp.json().catch(() => ({}))) as { error?: string }
  if (!resp.ok) {
    throw new Error(
      json.error || `delete webdev endpoint failed: ${resp.status} ${resp.statusText}`
    )
  }
  invalidateTagAcquisitionQueries()
}

export function useDeleteWebDevEndpointMutation(): [
  (gatewayUrl: string) => Promise<void>,
  MutationState
] {
  return useMutation<string, void>(deleteWebDevEndpoint)
}

// Re-export the pure `db://` grammar/validation helpers from `@atbash-labs/ontology-db`
// so UI consumers (the DB-workbench "save as data point" dialog) can preview the
// canonical path and pre-check SELECT-only without taking a direct dependency on
// the db package — this state package already depends on it, and re-exporting keeps
// one source of truth for the slug/path/validation rules.
export {
  buildDbTagPath,
  isDbTagPath,
  isSelectOnlyQuery,
  slugifyDatapoint
} from '@atbash-labs/ontology-db'
export type { DbSyncMode, DbTransport } from '@atbash-labs/ontology-db'

// ---------------------------------------------------------------------------
// Historian throughput / storage (debug-only diagnostic)
// ---------------------------------------------------------------------------

/**
 * `GET /ontology/tags:historianStatus`. Throughput is the caller org's
 * `historian_readings` rows over the trailing 5 minutes; storage is the whole
 * (shared) historian database. No storage capacity is configured anywhere the
 * proxy can read, so there is deliberately no percent-full figure.
 */
export type HistorianStatus =
  | { configured: false; reason: string; observedAt: string | null }
  | {
      configured: true
      observedAt: string | null
      throughput: {
        windowSeconds: number
        readings: number
        updatesPerSecond: number
      }
      storage: {
        databaseBytes: number
        /** `historian_readings` hypertable size; null on plain Postgres. */
        readingsBytes: number | null
        timescale: boolean
        /** Live retention policy; null = no policy (readings kept forever). */
        retentionDays: number | null
        capacityConfigured: boolean
      }
    }

export function historianStatusFromJson(raw: unknown): HistorianStatus {
  const j = (raw ?? {}) as Record<string, unknown>
  const observedAt = nullableStr(j.observed_at)
  if (j.configured !== true) {
    return {
      configured: false,
      reason: nullableStr(j.reason) ?? 'historian not configured',
      observedAt
    }
  }
  const t = (j.throughput ?? {}) as Record<string, unknown>
  const s = (j.storage ?? {}) as Record<string, unknown>
  return {
    configured: true,
    observedAt,
    throughput: {
      windowSeconds: num(t.window_seconds),
      readings: num(t.readings),
      updatesPerSecond: num(t.updates_per_second)
    },
    storage: {
      databaseBytes: num(s.database_bytes),
      readingsBytes: nullableNum(s.readings_bytes),
      timescale: s.timescale === true,
      retentionDays: nullableNum(s.retention_days),
      capacityConfigured: s.capacity_configured === true
    }
  }
}

export async function fetchHistorianStatus(signal?: AbortSignal): Promise<HistorianStatus> {
  return historianStatusFromJson(
    await requestJson<Record<string, unknown>>('/ontology/tags:historianStatus', { signal })
  )
}

/** Default refresh cadence for the debug historian readout. */
export const HISTORIAN_STATUS_POLL_MS = 15_000

/**
 * Reads the historian diagnostic and re-reads it every `pollMs` while not
 * skipped. Skipping stops both the request and the timer.
 */
export function useHistorianStatusQuery(
  opts: { skip?: boolean; pollMs?: number } = {}
): QueryResult<HistorianStatus> {
  const skip = opts.skip === true
  const pollMs = opts.pollMs ?? HISTORIAN_STATUS_POLL_MS
  const result = useQuery<undefined, HistorianStatus>(
    undefined,
    (_arg, signal) => fetchHistorianStatus(signal),
    'historian-status',
    undefined,
    { skip }
  )
  const { refetch } = result
  useEffect(() => {
    if (skip || pollMs <= 0) return
    const timer = setInterval(refetch, pollMs)
    return () => clearInterval(timer)
  }, [skip, pollMs, refetch])
  return result
}
